import { Fragment, useState } from 'react';
import { Button, Card, CircularProgress, Column, Divider, Row, Typography } from '@tempmee/react';
import { DiscountCard } from '@tempmee/react-blocks';
import { colors } from '@tempmee/tailwind';
import { useGetDiscountCodes } from '~/domain/DiscountCode';
import { AddDiscountCodeDialog } from './AddDiscountCodeDialog';
import { toDiscountCardProps } from './discountCodeView';

const EMPTY_COPY = 'No promo codes yet. Add a code to save on your GoTu booking fee.';
const ERROR_COPY = 'We could not load your promo codes. Please try again later.';

/**
 * Promo Codes section on Payment Information (GOTU-5656) — the office's discount
 * code history, the codeless discounts that target it (GOTU-19168), and the
 * entry point for adding a code.
 *
 * Read-only by design: a code, once applied, is never revoked from the office
 * side, so there is no remove action. Rows are `DiscountCard`s from the design
 * system, which own their own layout and status chip; this component owns the
 * section chrome, the list states, and opening the add dialog.
 *
 * @param officeId - the location whose codes these are; the selected office when omitted
 */
export const DiscountCodeSection = ({ officeId }: { officeId?: string }) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { discountCodes, isLoading, isError } = useGetDiscountCodes({ officeId });

  const hasCodes = discountCodes.length > 0;

  return (
    <Card elevated className="p-6" data-cy="promo-codes-section">
      <Row align="center" justify="between" gap={4}>
        <Typography variant="h6" color={colors.text.primary}>
          Promo Codes
        </Typography>
        <Button
          variant="outlined"
          onClick={() => setIsDialogOpen(true)}
          data-cy="promo-codes-add-button"
        >
          Add Promo Code
        </Button>
      </Row>

      <Divider className="mt-4" />

      {isLoading && (
        <Row align="center" justify="center" className="py-6">
          <CircularProgress />
        </Row>
      )}

      {!isLoading && isError && (
        <Row align="center" justify="center" className="py-6">
          <Typography variant="body2" color={colors.text.secondary} data-cy="promo-codes-error">
            {ERROR_COPY}
          </Typography>
        </Row>
      )}

      {!isLoading && !isError && !hasCodes && (
        <Row align="center" justify="center" className="py-6">
          <Typography variant="body2" color={colors.text.secondary} data-cy="promo-codes-empty">
            {EMPTY_COPY}
          </Typography>
        </Row>
      )}

      {!isLoading && !isError && hasCodes && (
        // Caps the section's height so a long history scrolls inside the card
        // instead of pushing the rest of Payment Information off the page.
        <Column className="max-h-96 overflow-y-auto pr-2" data-cy="promo-codes-list">
          {/* Server order is pay order — rendered as sent, never re-sorted. Keyed on
              campaignId: a codeless row's appliedCodeId is its campaignId, so the
              campaign is the one id unique across coded and codeless rows. */}
          {discountCodes.map((discountCode, index) => (
            <Fragment key={discountCode.campaignId}>
              {index > 0 && <Divider />}
              <DiscountCard {...toDiscountCardProps(discountCode)} data-cy="promo-code-card" />
            </Fragment>
          ))}
        </Column>
      )}

      <AddDiscountCodeDialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        officeId={officeId}
      />
    </Card>
  );
};
